import { Directive, ElementRef, Input, NgZone, OnInit } from '@angular/core';
import { SelectDateTimeService } from '@app/components/booking-steps/select-date-time/select-date-time.service';

@Directive({
    selector: '[horizontalScroll]',
    exportAs: 'horizontalScroll',
    standalone: false,
})
export class HorizontalScrollDirective implements OnInit {
    @Input('horizontalScroll') tagName!: string;

    private mouseDown = false;
    private startX = 0;
    private scrollLeft = 0;

    private isDragging = false;
    private suppressNextClick = false;

    private scrollStartPos: number | null = null;
    private scrollEndPos: number | null = null;

    private elementEl: HTMLElement;
    private horizontalBox: HTMLElement | null = null;

    constructor(
        private el: ElementRef,
        private zone: NgZone,
        private selectDateTimeService: SelectDateTimeService
    ) {
        this.elementEl = this.el.nativeElement;
    }

    ngOnInit() {
        this.horizontalBox = document.querySelector('.horizontal-scroll-box');

        if (this.isMobile()) {
            this.elementEl.style.touchAction = 'none';
        }

        this.initializeListeners();
    }

    private initializeListeners() {
        /** Run ALL scroll/touch/mouse events outside Angular */
        this.zone.runOutsideAngular(() => {
            /** WHEEL (mouse and trackpad) */
            this.elementEl.addEventListener('wheel', (event: WheelEvent) => {
                if (!this.horizontalBox) {
                    return;
                }

                const scrollDelta = event.deltaX !== 0 ? event.deltaX : event.deltaY;

                this.horizontalBox.scrollLeft += scrollDelta;

                const direction = this.getWheelDirection(event);
                if (direction === 'right') {
                    this.zone.run(() => this.selectDateTimeService.nextClicked(true));
                } else if (direction === 'left') {
                    this.zone.run(() => this.selectDateTimeService.previousClicked(true));
                }

                this.updateArrowVisibility();
                event.preventDefault();
            });

            /** POINTER DOWN */
            this.elementEl.addEventListener('pointerdown', (event: PointerEvent) => {
                this.mouseDown = true;
                this.isDragging = false;
                this.suppressNextClick = false;

                this.startX = event.clientX;
                this.scrollLeft = this.elementEl.scrollLeft;
                this.scrollStartPos = this.elementEl.scrollLeft;
            });

            /** POINTER MOVE */
            this.elementEl.addEventListener('pointermove', (event: PointerEvent) => {
                if (!this.mouseDown) {
                    return;
                }

                // No button is held any more, so the press ended without our pointerup firing
                if (event.buttons === 0) {
                    this.mouseDown = false;
                    return;
                }

                const diff = event.clientX - this.startX;

                if (!this.isDragging) {
                    if (Math.abs(diff) <= 5) {
                        return;
                    }

                    // Capture only once it is a drag, so a plain tap or click still lands on the item under the pointer
                    this.isDragging = true;
                    this.elementEl.setPointerCapture(event.pointerId);
                }

                this.elementEl.scrollLeft = this.scrollLeft - diff;

                this.updateArrowVisibility();
            });

            /** POINTER UP */
            this.elementEl.addEventListener('pointerup', (event: PointerEvent) => {
                if (!this.mouseDown) {
                    return;
                }

                this.mouseDown = false;
                this.scrollEndPos = this.elementEl.scrollLeft;

                if (this.isDragging) {
                    this.isDragging = false;
                    this.elementEl.releasePointerCapture(event.pointerId);
                    // A mouse clicks on release however far it moved, so swallow that click. Touch is left alone:
                    // the browser only clicks when it judged the gesture a tap, even if it drifted past our threshold.
                    this.suppressNextClick = event.pointerType !== 'touch';
                }

                this.detectScrollDirection();
            });

            /** POINTER CANCEL / LEAVE (a press that leaves before it becomes a drag is no longer tracked) */
            this.elementEl.addEventListener('pointercancel', () => {
                this.mouseDown = false;
                this.isDragging = false;
            });

            this.elementEl.addEventListener('pointerleave', () => {
                if (!this.isDragging) {
                    this.mouseDown = false;
                }
            });

            /** CLICK (capture phase, so it runs before the item's own click handler) */
            this.elementEl.addEventListener(
                'click',
                (event: MouseEvent) => {
                    // detail is 0 for keyboard and programmatic clicks, which never come from a drag
                    if (this.suppressNextClick && event.detail > 0) {
                        this.suppressNextClick = false;
                        event.preventDefault();
                        event.stopPropagation();
                    }
                },
                { capture: true }
            );
        });
    }

    /** Detect wheel direction (mouse and trackpad) */
    private getWheelDirection(event: WheelEvent): 'left' | 'right' | null {
        // Trackpad horizontal gesture (dominant deltaX)
        if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
            return event.deltaX > 0 ? 'right' : 'left';
        }

        // Mouse wheel vertical scroll converted to horizontal
        if (event.deltaY !== 0) {
            return event.deltaY > 0 ? 'right' : 'left';
        }

        return null;
    }

    private detectScrollDirection() {
        if (this.scrollStartPos === null || this.scrollEndPos === null) {
            return;
        }

        if (this.scrollEndPos > this.scrollStartPos) {
            this.zone.run(() => this.selectDateTimeService.nextClicked(true)); // Scroll right
        } else if (this.scrollEndPos < this.scrollStartPos) {
            this.zone.run(() => this.selectDateTimeService.previousClicked(true)); // Scroll left
        }
    }

    /** Update arrows */
    private updateArrowVisibility = (() => {
        let ticking = false;

        return () => {
            if (ticking) {
                return;
            }

            requestAnimationFrame(() => {
                const box = this.horizontalBox;
                if (!box) {
                    return;
                }

                const maxScroll = box.scrollWidth - box.clientWidth;
                const isAtStart = box.scrollLeft <= 0;
                const isAtEnd = box.scrollLeft >= maxScroll;

                const arrows = document.querySelectorAll('.icon .mat-icon');
                if (arrows.length < 2) {
                    return;
                }

                arrows[0].parentElement!.style.display = isAtStart ? 'none' : 'flex';
                arrows[1].parentElement!.style.display = isAtEnd ? 'none' : 'flex';

                ticking = false;
            });

            ticking = true;
        };
    })();

    /** Arrow button scroll animation */
    public scrollOnClick(direction: 'left' | 'right') {
        const element = this.elementEl;
        const amount = direction === 'left' ? -250 : 250;

        this.zone.runOutsideAngular(() => {
            const start = element.scrollLeft;
            const end = start + amount;
            const duration = 250;
            let startTime: number | null = null;

            const animate = (timestamp: number) => {
                if (!startTime) {
                    startTime = timestamp;
                }

                const progress = Math.min((timestamp - startTime) / duration, 1);
                element.scrollLeft = start + amount * progress;

                this.updateArrowVisibility();

                if (progress < 1) {
                    requestAnimationFrame(animate);
                }
            };

            requestAnimationFrame(animate);
        });
    }

    private isMobile(): boolean {
        return window.matchMedia('(pointer: coarse)').matches;
    }
}
